import React, { useEffect, useState } from "react";
import {
  getPipelineStages,
  updatePipelineStage,
} from "../api";

export default function PipelineStages() {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [color, setColor] = useState("#64748b");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadStages();
  }, []);

  async function loadStages() {
    try {
      setLoading(true);

      const response = await getPipelineStages();

      setStages(
        Array.isArray(response?.data)
          ? response.data
          : []
      );
    } catch (error) {
      console.error(
        "PIPELINE STAGES LOAD ERROR:",
        error
      );

      setStages([]);
    } finally {
      setLoading(false);
    }
  }

  function openEditModal(stage) {
    setEditingStage(stage);
    setDisplayName(stage.display_name || "");
    setColor(stage.color || "#64748b");
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setEditingStage(null);
    setDisplayName("");
    setColor("#64748b");
    setSaving(false);
  }

  async function saveStage() {
    if (!editingStage) return;

    if (!displayName.trim()) {
      window.alert("Stage name is required.");
      return;
    }

    try {
      setSaving(true);

      await updatePipelineStage(
        editingStage.id,
        displayName.trim(),
        color
      );

      await loadStages();
      closeModal();
    } catch (error) {
      console.error(
        "PIPELINE STAGE UPDATE ERROR:",
        error
      );

      window.alert(
        error?.response?.data?.message ||
          "Failed to update pipeline stage."
      );

      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div style={{ padding: "30px" }}>
      <div style={{ marginBottom: "24px" }}>
        <h2 style={{ marginBottom: "6px" }}>
          Pipeline Stage Management
        </h2>

        <p
          style={{
            margin: 0,
            color: "#64748b",
          }}
        >
          Rename pipeline labels without changing
          internal stage values.
        </p>
      </div>

      <div
        style={{
          background: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
          }}
        >
          <thead>
            <tr
              style={{
                background: "#f8fafc",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <th style={headerStyle}>Order</th>
              <th style={headerStyle}>Display Name</th>
              <th style={headerStyle}>
                Internal Value
              </th>
              <th style={headerStyle}>Description</th>
              <th style={headerStyle}>Color</th>
              <th style={headerStyle}>Action</th>
            </tr>
          </thead>

          <tbody>
            {stages.map((stage) => (
              <tr
                key={stage.id}
                style={{
                  borderBottom:
                    "1px solid #f1f5f9",
                }}
              >
                <td style={cellStyle}>
                  {stage.sort_order}
                </td>

                <td style={cellStyle}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "9px",
                      fontWeight: 600,
                    }}
                  >
                    <span
                      style={{
                        width: "10px",
                        height: "10px",
                        borderRadius: "50%",
                        background:
                          stage.color || "#64748b",
                        flexShrink: 0,
                      }}
                    />

                    {stage.display_name}
                  </div>
                </td>

                <td style={cellStyle}>
                  <code
                    style={{
                      background: "#f1f5f9",
                      padding: "4px 7px",
                      borderRadius: "5px",
                      fontSize: "12px",
                    }}
                  >
                    {stage.stored_value}
                  </code>
                </td>

                <td style={cellStyle}>
                  {stage.description || "-"}
                </td>

                <td style={cellStyle}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span
                      style={{
                        width: "24px",
                        height: "24px",
                        borderRadius: "6px",
                        background:
                          stage.color || "#64748b",
                        border: "1px solid #e5e7eb",
                      }}
                    />

                    <span>{stage.color}</span>
                  </div>
                </td>

                <td style={cellStyle}>
                  <button
                    onClick={() =>
                      openEditModal(stage)
                    }
                    style={{
                      background: "#2563eb",
                      color: "#ffffff",
                      border: "none",
                      padding: "8px 18px",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}

            {stages.length === 0 && (
              <tr>
                <td
                  colSpan="6"
                  style={{
                    padding: "35px",
                    textAlign: "center",
                    color: "#94a3b8",
                  }}
                >
                  No pipeline stages found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && editingStage && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.45)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
          onClick={closeModal}
        >
          <div
            style={{
              width: "420px",
              background: "#ffffff",
              padding: "26px",
              borderRadius: "12px",
              boxShadow:
                "0 20px 45px rgba(15, 23, 42, 0.2)",
            }}
            onClick={(event) =>
              event.stopPropagation()
            }
          >
            <h3 style={{ marginTop: 0 }}>
              Edit Pipeline Stage
            </h3>

            <div style={{ marginTop: "18px" }}>
              <label style={labelStyle}>
                Display Name
              </label>

              <input
                value={displayName}
                onChange={(event) =>
                  setDisplayName(event.target.value)
                }
                placeholder="Display Name"
                style={inputStyle}
                autoFocus
              />
            </div>

            <div style={{ marginTop: "16px" }}>
              <label style={labelStyle}>
                Internal Value
              </label>

              <input
                value={editingStage.stored_value}
                disabled
                style={{
                  ...inputStyle,
                  background: "#f1f5f9",
                  color: "#64748b",
                  cursor: "not-allowed",
                }}
              />
            </div>

            <div style={{ marginTop: "16px" }}>
              <label style={labelStyle}>
                Stage Color
              </label>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <input
                  type="color"
                  value={color}
                  onChange={(event) =>
                    setColor(event.target.value)
                  }
                  style={{
                    width: "52px",
                    height: "42px",
                    padding: "3px",
                    border: "1px solid #cbd5e1",
                    borderRadius: "7px",
                    cursor: "pointer",
                  }}
                />

                <input
                  value={color}
                  onChange={(event) =>
                    setColor(event.target.value)
                  }
                  style={inputStyle}
                  placeholder="#64748b"
                />
              </div>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                marginTop: "24px",
              }}
            >
              <button
                onClick={closeModal}
                disabled={saving}
                style={{
                  padding: "10px 18px",
                  borderRadius: "8px",
                  border: "1px solid #cbd5e1",
                  background: "#ffffff",
                  cursor: saving
                    ? "not-allowed"
                    : "pointer",
                }}
              >
                Cancel
              </button>

              <button
                onClick={saveStage}
                disabled={saving}
                style={{
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: "none",
                  background: "#16a34a",
                  color: "#ffffff",
                  fontWeight: 600,
                  cursor: saving
                    ? "not-allowed"
                    : "pointer",
                  opacity: saving ? 0.7 : 1,
                }}
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const headerStyle = {
  padding: "14px 16px",
  textAlign: "left",
  fontSize: "12px",
  color: "#475569",
  fontWeight: 700,
};

const cellStyle = {
  padding: "16px",
  fontSize: "14px",
  color: "#1e293b",
};

const labelStyle = {
  display: "block",
  marginBottom: "7px",
  fontSize: "13px",
  fontWeight: 600,
  color: "#334155",
};

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "11px 12px",
  border: "1px solid #cbd5e1",
  borderRadius: "7px",
  outline: "none",
};